<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';
import { CHAR_LENGTH_WARNING } from './constants';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  isEditorDisabled: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: Number,
    default: null,
  },
  isMessageLengthReachingThreshold: {
    type: Boolean,
    default: false,
  },
  charactersRemaining: {
    type: Number,
    default: 0,
  },
  editorContent: {
    type: String,
    default: undefined,
  },
  hasContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleEditorSize', 'executeCopilotAction']);

const { captainTasksEnabled } = useCaptain();

const showCopilotMenu = ref(false);
const copilotToggleRef = ref(null);

const charLengthClass = computed(() =>
  props.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11'
);

const characterLengthWarning = computed(() =>
  props.charactersRemaining < 0
    ? `${-props.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
    : `${props.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`
);

const handleCopilotAction = (actionKey, data) => {
  emit('executeCopilotAction', actionKey, data || props.editorContent);
  showCopilotMenu.value = false;
};

const toggleCopilotMenu = () => {
  const isOpening = !showCopilotMenu.value;
  if (isOpening) {
    useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
      conversationId: props.conversationId,
      entryPoint: 'top_panel',
    });
  }
  showCopilotMenu.value = isOpening;
};

const handleClickOutside = () => {
  showCopilotMenu.value = false;
};
</script>

<template>
  <div class="flex items-center flex-shrink-0 gap-2">
    <div v-if="isMessageLengthReachingThreshold" class="text-xs">
      <span :class="charLengthClass">
        {{ characterLengthWarning }}
      </span>
    </div>
    <div v-if="captainTasksEnabled" class="relative flex">
      <NextButton
        ref="copilotToggleRef"
        ghost
        :disabled="disabled || isEditorDisabled"
        :class="{
          'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
          'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
        }"
        sm
        icon="i-ph-sparkle-fill"
        @click="toggleCopilotMenu"
      />
      <CopilotMenuBar
        v-if="showCopilotMenu"
        v-on-click-outside="[
          handleClickOutside,
          { ignore: [copilotToggleRef] },
        ]"
        :has-selection="false"
        :has-content="hasContent"
        :conversation-id="conversationId"
        class="ltr:right-0 rtl:left-0 bottom-full mb-2"
        @execute-copilot-action="handleCopilotAction"
      />
    </div>
    <NextButton
      ghost
      class="text-n-slate-11"
      sm
      icon="i-lucide-maximize-2"
      @click="emit('toggleEditorSize')"
    />
  </div>
</template>
